/* ==========================================================================
   AURA STORE — WISHLIST ENGINE V1
   Path: assets/aura-wishlist.js

   Native, app-free wishlist engine.
   - localStorage persistence
   - migration from legacy `aura_wishlist`
   - snapshots for instant wishlist rendering
   - cross-tab synchronization
   - reusable button state synchronization
   - header count synchronization
   - neutral custom events
   ========================================================================== */

(function () {
  'use strict';

  if (
    window.AuraWishlist &&
    window.AuraWishlist.version === '1.0.0'
  ) {
    return;
  }

  var STORAGE_KEY = 'aura_wishlist_v1';
  var LEGACY_KEY = 'aura_wishlist';
  var MAX_ITEMS = 100;

  function now() {
    return Date.now();
  }

  function normalizeHandle(value) {
    return String(value || '')
      .trim()
      .replace(/^\/+|\/+$/g, '');
  }

  function normalizeItem(raw) {
    if (!raw) {
      return null;
    }

    if (typeof raw === 'string') {
      var legacyHandle = normalizeHandle(raw);

      if (!legacyHandle) {
        return null;
      }

      return {
        handle: legacyHandle,
        addedAt: now(),
        snapshot: null
      };
    }

    var handle = normalizeHandle(raw.handle);

    if (!handle) {
      return null;
    }

    var snapshot = null;

    if (
      raw.snapshot &&
      typeof raw.snapshot === 'object'
    ) {
      snapshot = {
        id:
          raw.snapshot.id != null
            ? raw.snapshot.id
            : null,

        title:
          raw.snapshot.title
            ? String(raw.snapshot.title)
            : '',

        vendor:
          raw.snapshot.vendor
            ? String(raw.snapshot.vendor)
            : '',

        url:
          raw.snapshot.url
            ? String(raw.snapshot.url)
            : '',

        image:
          raw.snapshot.image
            ? String(raw.snapshot.image)
            : '',

        price:
          Number.isFinite(Number(raw.snapshot.price))
            ? Number(raw.snapshot.price)
            : null,

        compareAtPrice:
          Number.isFinite(Number(raw.snapshot.compareAtPrice))
            ? Number(raw.snapshot.compareAtPrice)
            : null
      };
    }

    return {
      handle: handle,

      addedAt:
        Number.isFinite(Number(raw.addedAt))
          ? Number(raw.addedAt)
          : now(),

      snapshot: snapshot
    };
  }

  function dedupe(items) {
    var seen = new Set();
    var result = [];

    items.forEach(function (item) {
      var normalized = normalizeItem(item);

      if (
        !normalized ||
        seen.has(normalized.handle)
      ) {
        return;
      }

      seen.add(normalized.handle);
      result.push(normalized);
    });

    return result
      .sort(function (a, b) {
        return b.addedAt - a.addedAt;
      })
      .slice(0, MAX_ITEMS);
  }

  function readRaw(key) {
    try {
      var value =
        window.localStorage.getItem(key);

      if (!value) {
        return null;
      }

      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function write(items) {
    var normalized = dedupe(items);

    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(normalized)
      );
    } catch (error) {}

    return normalized;
  }

  function migrateLegacy() {
    var current = readRaw(STORAGE_KEY);

    if (Array.isArray(current)) {
      return write(current);
    }

    var legacy = readRaw(LEGACY_KEY);

    if (!Array.isArray(legacy)) {
      return write([]);
    }

    var migrated =
      legacy.map(function (handle, index) {
        return {
          handle: normalizeHandle(handle),
          addedAt: now() - index,
          snapshot: null
        };
      });

    var saved = write(migrated);

    try {
      window.localStorage.removeItem(
        LEGACY_KEY
      );
    } catch (error) {}

    return saved;
  }

  var items = migrateLegacy();

  function getAll() {
    return items.map(function (item) {
      return {
        handle: item.handle,
        addedAt: item.addedAt,
        snapshot:
          item.snapshot
            ? Object.assign({}, item.snapshot)
            : null
      };
    });
  }

  function count() {
    return items.length;
  }

  function findIndex(handle) {
    var normalized = normalizeHandle(handle);

    return items.findIndex(function (item) {
      return item.handle === normalized;
    });
  }

  function has(handle) {
    return findIndex(handle) !== -1;
  }

  function emit(detail) {
    var payload =
      Object.assign(
        {
          items: getAll(),
          count: count()
        },
        detail || {}
      );

    document.dispatchEvent(
      new CustomEvent(
        'aura:wishlist:change',
        {
          detail: payload
        }
      )
    );

    /*
      Legacy compatibility with PDP V8 listeners / analytics hooks.
    */
    document.dispatchEvent(
      new CustomEvent(
        'aura:wishlist',
        {
          detail: payload
        }
      )
    );
  }

  function persistAndSync(detail) {
    items = write(items);

    syncDom();
    emit(detail);

    return getAll();
  }

  function add(handle, snapshot, meta) {
    var normalized = normalizeHandle(handle);

    if (!normalized) {
      return false;
    }

    var index = findIndex(normalized);

    if (index !== -1) {
      if (snapshot) {
        items[index].snapshot =
          normalizeItem({
            handle: normalized,
            snapshot: snapshot
          }).snapshot;

        items[index].addedAt =
          items[index].addedAt || now();

        persistAndSync({
          action: 'update',
          handle: normalized,
          saved: true,
          source:
            meta && meta.source
              ? meta.source
              : 'unknown'
        });
      }

      return true;
    }

    var normalizedItem =
      normalizeItem({
        handle: normalized,
        addedAt: now(),
        snapshot: snapshot || null
      });

    items.unshift(normalizedItem);

    persistAndSync({
      action: 'add',
      handle: normalized,
      saved: true,
      source:
        meta && meta.source
          ? meta.source
          : 'unknown'
    });

    return true;
  }

  function remove(handle, meta) {
    var normalized = normalizeHandle(handle);
    var index = findIndex(normalized);

    if (index === -1) {
      return false;
    }

    items.splice(index, 1);

    persistAndSync({
      action: 'remove',
      handle: normalized,
      saved: false,
      source:
        meta && meta.source
          ? meta.source
          : 'unknown'
    });

    return true;
  }

  function toggle(handle, snapshot, meta) {
    if (has(handle)) {
      remove(handle, meta);
      return false;
    }

    add(handle, snapshot, meta);
    return true;
  }

  function clear(meta) {
    items = [];

    persistAndSync({
      action: 'clear',
      saved: false,
      source:
        meta && meta.source
          ? meta.source
          : 'unknown'
    });
  }

  function getSnapshotFromButton(button) {
    if (!button) {
      return null;
    }

    var price =
      button.getAttribute(
        'data-product-price'
      );

    var compareAtPrice =
      button.getAttribute(
        'data-product-compare-at-price'
      );

    return {
      id:
        button.getAttribute(
          'data-product-id'
        ) || null,

      title:
        button.getAttribute(
          'data-product-title'
        ) || '',

      vendor:
        button.getAttribute(
          'data-product-vendor'
        ) || '',

      url:
        button.getAttribute(
          'data-product-url'
        ) || '',

      image:
        button.getAttribute(
          'data-product-image'
        ) || '',

      price:
        price != null &&
        price !== ''
          ? Number(price)
          : null,

      compareAtPrice:
        compareAtPrice != null &&
        compareAtPrice !== ''
          ? Number(compareAtPrice)
          : null
    };
  }

  function syncButton(button) {
    var handle =
      button.getAttribute(
        'data-product-handle'
      );

    if (!handle) {
      return;
    }

    var saved = has(handle);

    button.classList.toggle(
      'is-saved',
      saved
    );

    button.setAttribute(
      'aria-pressed',
      saved ? 'true' : 'false'
    );

    var addLabel =
      button.getAttribute(
        'data-label-add'
      ) ||
      'Ajouter à la wishlist';

    var removeLabel =
      button.getAttribute(
        'data-label-remove'
      ) ||
      'Retirer de la wishlist';

    button.setAttribute(
      'aria-label',
      saved
        ? removeLabel
        : addLabel
    );

    var label =
      button.querySelector(
        '[data-aura-wishlist-label]'
      );

    if (label) {
      label.textContent =
        saved
          ? removeLabel
          : addLabel;
    }
  }

  function syncCounts() {
    Array.prototype.slice.call(
      document.querySelectorAll(
        '[data-aura-wishlist-count]'
      )
    ).forEach(function (node) {
      node.textContent =
        String(count());

      node.toggleAttribute(
        'hidden',
        count() === 0
      );
    });
  }

  function syncDom() {
    Array.prototype.slice.call(
      document.querySelectorAll(
        '[data-aura-wishlist-button]'
      )
    ).forEach(syncButton);

    syncCounts();
  }

  function handleClick(event) {
    var button =
      event.target.closest
        ? event.target.closest(
            '[data-aura-wishlist-button]'
          )
        : null;

    if (!button) {
      return;
    }

    event.preventDefault();

    var handle =
      button.getAttribute(
        'data-product-handle'
      );

    if (!handle) {
      return;
    }

    var snapshot =
      getSnapshotFromButton(
        button
      );

    toggle(
      handle,
      snapshot,
      {
        source:
          button.getAttribute(
            'data-wishlist-source'
          ) ||
          'button'
      }
    );
  }

  document.addEventListener(
    'click',
    handleClick
  );

  document.addEventListener(
    'shopify:section:load',
    syncDom
  );

  document.addEventListener(
    'DOMContentLoaded',
    syncDom
  );

  window.addEventListener(
    'storage',
    function (event) {
      if (
        event.key !== STORAGE_KEY &&
        event.key !== LEGACY_KEY
      ) {
        return;
      }

      items =
        dedupe(
          readRaw(STORAGE_KEY) ||
          []
        );

      syncDom();

      emit({
        action: 'sync',
        source: 'storage'
      });
    }
  );

  window.AuraWishlist = {
    version: '1.0.0',
    storageKey: STORAGE_KEY,
    getAll: getAll,
    count: count,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clear,
    sync: syncDom
  };

  syncDom();

  document.dispatchEvent(
    new CustomEvent(
      'aura:wishlist:ready',
      {
        detail: {
          count: count()
        }
      }
    )
  );
})();